import { h, defineComponent, VNodeChildren } from 'vue';
import { Task } from '@/ui/concerns/useTask';
import { t } from '@/ui/i18n/i18n';
import { isPermanentError } from '@/services/api';
import { apiErrorMessage } from './message/ApiErrorMessage';
import { button } from './Button';
import { pushRoute } from '@/ui/helpers';
import { Loading } from './Loading';

export interface LoaderProps {
  task: Task<unknown>;
  tag: keyof HTMLElementTagNameMap;
  showContents?: boolean;
  taskParams?: unknown;
  taskSuspended?: boolean;
}

export const Loader = defineComponent<LoaderProps>({
  name: 'Loader',
  props: ['task', 'tag', 'showContents', 'taskParams', 'taskSuspended'],
  emits: ['task-run'],
  setup(props, context) {
    if (!props.taskSuspended) {
      props.taskParams ? props.task.run(props.taskParams) : props.task.run();
      if (context.listeners['task-run']) {
        context.emit('task-run');
      }
    }

    return function render() {
      let content: VNodeChildren = [];

      if ('error' in props.task.state.value) {
        if (context.slots.error) {
          content = context.slots.error();
        } else {
          content = [
            apiErrorMessage(props.task.state.value),
            isPermanentError(props.task.state.value.error.code)
              ? button(t('common.go-home'), () => pushRoute({ name: 'home' }))
              : button(t('common.retry'), () => context.emit('retry')),
          ];
        }
      } else {
        content = [
          h(Loading, { props: { active: props.task.state.value.pending } }),
          props.task.state.value.pending && !props.showContents
            ? h('div', { style: 'height: 200px' })
            : context.slots.default?.(),
        ];
      }

      return h(
        props.tag,
        { class: { loading: props.task.state.value.pending } },
        content,
      );
    };
  },
});

export function loader(
  tag: keyof HTMLElementTagNameMap,
  task: Task<unknown> | undefined,
  contents: VNodeChildren,
  showContents = true,
  tagClass = '',
  tagProps = {},
) {
  if (task === undefined) {
    return h(tag, {
      class: tagClass,
      props: { ...tagProps },
    }, contents);
  }

  const props: LoaderProps = {
    tag,
    task,
    showContents,
    ...tagProps,
  };
  return h(Loader, {
    class: tagClass,
    props,
    on: { retry: task.run },
  }, contents);
}

export function filterFormLoader(
  task: Task<unknown> | undefined,
  contents: VNodeChildren,
  showContents = true,
  classes = '',
) {
  return loader('form', task, contents, showContents, `flex relative filters-form ${classes}`);
}
